/**
 * Section Data Hooks
 *
 * React hooks for fetching and managing section data from API.
 * Integrates with MVP 1.20 backend endpoints.
 *
 * Epic: MVP 1.20 - Dynamic Experience Page Layout
 */

import { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

/**
 * Fetch experience page structure (customer view)
 * @param {string} experienceId - Experience UUID
 * @param {number} categoryL1Id - L1 category ID
 * @returns {Object} { data, loading, error }
 */
export function useCustomerPage(experienceId, categoryL1Id, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !experienceId || !categoryL1Id) {return;}

    const fetchPage = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          `${API_BASE_URL}/api/v1/experiences/${experienceId}/page/customer?categoryL1Id=${categoryL1Id}`,
          {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }
        );
        if (!response.ok) {throw new Error('Failed to load page');}
        const result = await response.json();
        setData(result.page);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load page');
        console.error('Error loading customer page:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [enabled, experienceId, categoryL1Id]);

  return { data, loading, error };
}

/**
 * Fetch experience page structure (guide edit view)
 * @param {string} experienceId - Experience UUID
 * @param {number} categoryL1Id - L1 category ID
 * @returns {Object} { data, loading, error, refetch }
 */
export function useGuideEditPage(experienceId, categoryL1Id, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState(null);

  const fetchPage = useCallback(async () => {
    if (!enabled || !experienceId || !categoryL1Id) {return;}

    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/experiences/${experienceId}/page/edit?categoryL1Id=${categoryL1Id}`,
        {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );
      if (!response.ok) {throw new Error('Failed to load page');}
      const result = await response.json();
      setData(result.page);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load page');
      console.error('Error loading edit page:', err);
    } finally {
      setLoading(false);
    }
  }, [enabled, experienceId, categoryL1Id]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  return {
    data,
    loading,
    error,
    refetch: fetchPage
  };
}

/**
 * Fetch single section data
 * @param {string} experienceId - Experience UUID
 * @param {string} sectionTypeId - Section type UUID
 * @returns {Object} { data, loading, error }
 */
export function useSectionData(experienceId, sectionTypeId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!experienceId || !sectionTypeId) {return;}

    const fetchSection = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          `${API_BASE_URL}/api/v1/experiences/${experienceId}/sections/${sectionTypeId}`,
          {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }
        );
        if (response.status === 404) {
          setData(null); // Section doesn't exist yet
          setError(null);
        } else if (!response.ok) {
          throw new Error('Failed to load section');
        } else {
          const result = await response.json();
          setData(result.data);
          setError(null);
        }
      } catch (err) {
        setError(err.message || 'Failed to load section');
      } finally {
        setLoading(false);
      }
    };

    fetchSection();
  }, [experienceId, sectionTypeId]);

  return { data, loading, error };
}

/**
 * Save section data
 * @param {string} experienceId - Experience UUID
 * @param {string} sectionTypeId - Section type UUID
 * @returns {Object} { save, saving, error }
 */
export function useSaveSectionData(experienceId, sectionTypeId) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async (contentData, isDraft = true) => {
    try {
      setSaving(true);
      setError(null);

      const token = localStorage.getItem('accessToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/v1/experiences/${experienceId}/sections/${sectionTypeId}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ contentData, isDraft })
        }
      );

      if (!response.ok) {throw new Error('Failed to save section');}
      const result = await response.json();
      return result.data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to save section';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return { save, saving, error };
}

/**
 * Publish section
 * @param {string} experienceId - Experience UUID
 * @param {string} sectionTypeId - Section type UUID
 * @returns {Object} { publish, publishing, error }
 */
export function usePublishSection(experienceId, sectionTypeId) {
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);

  const publish = async () => {
    try {
      setPublishing(true);
      setError(null);

      const token = localStorage.getItem('accessToken');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/v1/experiences/${experienceId}/sections/${sectionTypeId}/publish`,
        {
          method: 'PUT',
          headers
        }
      );

      if (!response.ok) {throw new Error('Failed to publish section');}
      const result = await response.json();
      return result.data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to publish section';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setPublishing(false);
    }
  };

  return { publish, publishing, error };
}

/**
 * Get page completion stats
 * @param {string} experienceId - Experience UUID
 * @param {number} categoryL1Id - L1 category ID
 * @returns {Object} { stats, loading, error }
 */
export function usePageCompletionStats(experienceId, categoryL1Id) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!experienceId || !categoryL1Id) {return;}

    const fetchStats = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          `${API_BASE_URL}/api/v1/experiences/${experienceId}/page/completion?categoryL1Id=${categoryL1Id}`,
          {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }
        );
        if (!response.ok) {throw new Error('Failed to load stats');}
        const result = await response.json();
        setStats(result.stats);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [experienceId, categoryL1Id]);

  return { stats, loading, error };
}
